import React, { useEffect, useState, Fragment, useRef, useReducer } from "react";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Modal,
  Flex,
  Divider,
  Avatar,
  Text,
  Menu,
  SearchMenu,
  List,
  Checkbox
} from "@theorgchartdev/storybook";

import cx from "classnames";
import { GetUserList } from "@/Utils/Promises";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import { handleCopyToClipboard } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumPlanRoles, kUserPlanRoles } from "@coreapp/constants";
import { GeneratePlanLink } from "@coreapp/utils/GeneratePlanLink";
import { appstrp } from "@coreapp/utils/appstrp";
import { ShareWithHandler } from "../PlanBuilderModal/SharedWithHandler";
import { planFormReducer } from "@/Utils/Plan/NewPlanForm";

export const ShareDocModal = () => {
  const {
    shareContextValue: { showShareModal, shareData, closeShareModal, onSaveShare }
  } = useWSMgrContext();

  const [shareFormState, dispatch] = useReducer(planFormReducer, {
    sharedWith: [],
    emailInvite: false,
    defaultRole: enumPlanRoles.collaborator
  });

  const [shareQuery, setShareQuery] = useState("");
  const [sharedWithHandler, setSharedWithHandler] = useState(null);

  const searchCtrlRef = useRef(null);
  const openCtrlRef = useRef(null);

  useEffect(() => {
    if (!showShareModal) {
      dispatch({ type: "SET_EMAIL_INVITE", payload: false });
      dispatch({ type: "RESET_SHARED_WITH" });
      dispatch({ type: "SET_DEFAULT_ROLE", payload: enumPlanRoles.collaborator });
      return;
    }

    if (shareData?.usersWithAccess) {
      const formattedData = ShareWithHandler.formatExistingUsers(shareData.usersWithAccess);
      dispatch({ type: "SET_SHARED_WITH", payload: formattedData });
    }

    if (shareData?.emailInvite) {
      dispatch({ type: "SET_EMAIL_INVITE", payload: shareData.emailInvite });
    }

    // Ensure backward compatibility: null/undefined is collaborator
    const defaultRoleValue = shareData?.defaultRole || enumPlanRoles.collaborator;
    dispatch({ type: "SET_DEFAULT_ROLE", payload: defaultRoleValue });

    const asyncHander = async () => {
      const shareHandler = new ShareWithHandler();
      await shareHandler.loadUsersAndGroups();

      shareHandler.buildUserAndGroupOptions([], users => {
        dispatch({ type: "SET_SHARED_WITH", payload: users });
      });

      setSharedWithHandler(shareHandler);
    };

    asyncHander();
  }, [shareData, showShareModal]);

  if (!showShareModal) return;

  const handleSave = () => {
    const existingUserData = shareData?.usersWithAccess.reduce(
      (o, user) => ({ ...o, [user.name]: user }),
      {}
    );
    const formattedUsers = shareFormState.sharedWith.map(user => ({
      ...existingUserData[user.username],
      name: user.username,
      role: user.editorRole,
      displayName: user.name || existingUserData[user.username]?.displayName || user.username
    }));

    onSaveShare({
      users: formattedUsers,
      emailInvite: shareFormState.emailInvite,
      defaultRole: shareFormState.defaultRole
    });
    closeShareModal();
  };

  const onInput = inputValue => {
    setShareQuery(inputValue);
  };

  const onResetInput = () => {
    setShareQuery("");
  };

  const copyFeedback = (open = true) => {
    openCtrlRef?.current?.setOpen(open);
    if (open === true) setTimeout(() => copyFeedback(false), 4000);
  };

  const GenerateShareLink = () => {
    let theLink = GeneratePlanLink(shareData?.docId);
    handleCopyToClipboard(theLink);
    copyFeedback();
  };

  const showCopyLink = () => {
    let docId = shareData?.docId;
    return docId === null || docId === undefined;
  };

  const getHeaderTitle = () => {
    let title = appstr("Share");
    let docName = shareData?.name;
    if (docName) title += ' "' + docName + '"';
    return title;
  };

  const shareWithOptions =
    sharedWithHandler?.getShareWithOptions(shareQuery || "", shareFormState.sharedWith) ?? [];
  const sharedUserList =
    sharedWithHandler?.buildActiveSharedUserOptions(
      shareFormState.sharedWith,
      (username, role) => dispatch({ type: "SET_SHARED_WITH_ROLE", payload: { username, role } }),
      username => dispatch({ type: "REMOVE_SHARED_WITH", payload: username })
    ) ?? [];

  return (
    <CentralizedModalContainer show={showShareModal}>
      <Modal
        id="share-doc-modal"
        show={showShareModal}
        onClose={closeShareModal}
        minWidth={"medium"}
        maxWidth={"medium"}
        withShadow={true}
        centerScreen={true}
      >
        <DialogTitle divider={true} onClose={closeShareModal}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"share-nodes"} />
            {getHeaderTitle()}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content"}>
          <Flex direction={"column"} gap={"12px"} height={"100%"}>
            <SearchMenu
              placeholder={appstr("Add people or groups")}
              searchSize={"medium"}
              searchProps={{ onResetInput: onResetInput, dataTestId: "share-doc-search-input" }}
              options={shareWithOptions}
              optionSize={"small"}
              searchCallback={onInput}
              listWidth={"large"}
              loading={false}
              defaultInputVal={""}
              openCtrlRef={searchCtrlRef}
            />
            <Checkbox
              checked={shareFormState.defaultRole === enumPlanRoles.collaborator}
              id="default-role"
              indeterminate={false}
              label={appstr("Default users as collaborators")}
              onToggle={event =>
                dispatch({
                  type: "SET_DEFAULT_ROLE",
                  payload: event.target.checked ? enumPlanRoles.collaborator : enumPlanRoles.viewer
                })
              }
              size="medium"
            />
            <Divider />
            <Text size={"medium"} modifier={"medium"}>
              {appstr("People with access")}
            </Text>
            <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
              <List
                enableVirtualization={false}
                listWidth="fill"
                options={sharedUserList}
                showCheck={false}
              />
            </div>
            <Divider />
            <Checkbox
              checked={shareFormState.emailInvite}
              id="notify-users-share-plan"
              indeterminate={false}
              label={appstr("Notify people")}
              onToggle={event => {
                dispatch({ type: "SET_EMAIL_INVITE", payload: event.target.checked });
              }}
              size="medium"
            />
            <div style={{ marginLeft: 0, marginRight: "auto" }}>
              <ToolbarPopover
                openCtrlRef={openCtrlRef}
                otherPopoverProps={{
                  reference: (
                    <Button
                      size={"medium"}
                      modifier={"secondary"}
                      className={"oc-special-secondary"}
                      onClick={GenerateShareLink}
                      iconProps={{ name: "link" }}
                      disabled={showCopyLink()}
                    >
                      {appstr("Copy link")}
                    </Button>
                  )
                }}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <Icon modifier={"success"} name={"circle-check"} size={"small"} />
                  <Text size={"small"}>{appstrp("Link copied", "!")}</Text>
                </Flex>
              </ToolbarPopover>
            </div>
          </Flex>
        </DialogContent>
        {
          <DialogActions divider={true}>
            <DialogActions.Tertiary onClick={closeShareModal}>
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary onClick={handleSave}>{appstr("Save")}</DialogActions.Primary>
          </DialogActions>
        }
      </Modal>
    </CentralizedModalContainer>
  );
};
